import React, { useState } from 'react';
import {
  CL_TOKENS,
  INTENT_META,
  OUTBOUND_OUTCOME_META,
  OUTCOME_META,
  RECALL_TYPE_META,
  STATUS_META,
} from '../../receptionist-call-logs/config';
import type {
  AttemptLogEntry,
  ExtractedFact,
  InboundCall,
  OutboundPatientCall,
  RecallContext,
  TranscriptSpeakerTurn,
  TranscriptTurn,
} from '../../receptionist-call-logs';
import { isReasoningTurn } from '../../receptionist-call-logs';
import type { IconKind } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import { AudioPlayer } from './AudioPlayer';
import { ConfigPill, PatientAvatar, SentimentDot, getInitials } from './CallLogsShared';

/* ---- AI call summary ---------------------------------------------------- */
function AICallSummary({ points }: { points: string[] }) {
  return (
    <div
      className="rounded-[11px] border px-[18px] pb-3.5 pt-4"
      style={{ background: CL_TOKENS.greenWash, borderColor: CL_TOKENS.greenSoft }}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon kind="sparkle" size={14} color={CL_TOKENS.green} stroke={2} />
        <h3 className="m-0 text-[13px] font-bold tracking-[-0.01em]" style={{ color: CL_TOKENS.greenDeep }}>
          AI call summary
        </h3>
      </div>
      <div className="flex flex-col gap-[9px]">
        {points.map((p, i) => (
          <div key={i} className="grid grid-cols-[20px_1fr] items-start gap-2.5">
            <div
              className="mt-px inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[10.5px] font-bold"
              style={{ background: CL_TOKENS.greenSoft, color: CL_TOKENS.greenDeep }}
            >
              {i + 1}
            </div>
            <p className="m-0 text-[13px] font-medium leading-[1.55] tracking-[-0.005em] text-[var(--ink-0)]">{p}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Extracted facts ---------------------------------------------------- */
function ExtractedSection({ items, title = 'What Elva captured' }: { items: ExtractedFact[]; title?: string }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="cl-eyebrow mb-2.5">{title}</div>
      <div className="overflow-hidden rounded-[11px] border border-[var(--cream-2)] bg-[var(--cream-1)]">
        {items.map((it, i) => (
          <div
            key={i}
            className="grid grid-cols-[minmax(110px,auto)_1fr] items-baseline gap-3.5 px-3.5 py-2.5"
            style={{ borderBottom: i < items.length - 1 ? `1px solid ${CL_TOKENS.border}` : 'none' }}
          >
            <div className="text-[11.5px] font-semibold tracking-[-0.005em]" style={{ color: CL_TOKENS.ink3 }}>
              {it.label}
            </div>
            <div className="text-[12.5px] font-medium leading-[1.5] tracking-[-0.005em] text-[var(--ink-0)]">
              {it.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Transcript --------------------------------------------------------- */
function TranscriptTurnView({ turn }: { turn: TranscriptSpeakerTurn }) {
  const isElva = turn.speaker === 'elva';
  return (
    <div className="mb-3 grid grid-cols-[auto_1fr] gap-[11px]">
      <div
        className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-[10px] font-bold tracking-[0.04em]"
        style={{
          background: isElva ? CL_TOKENS.greenWash : CL_TOKENS.brassTint,
          color: isElva ? CL_TOKENS.greenDeep : CL_TOKENS.brassDeep,
          border: `1px solid ${isElva ? CL_TOKENS.greenSoft : CL_TOKENS.brassBorder}`,
        }}
      >
        {isElva ? 'E' : 'P'}
      </div>
      <div>
        <div className="mb-[3px] flex items-baseline gap-2">
          <span
            className="text-[11.5px] font-bold tracking-[-0.005em]"
            style={{ color: isElva ? CL_TOKENS.greenDeep : CL_TOKENS.brassDeep }}
          >
            {isElva ? 'Elva' : 'Patient'}
          </span>
          <span className="cl-mono text-[10.5px]" style={{ color: CL_TOKENS.ink4 }}>
            {turn.time}
          </span>
        </div>
        <div className="text-[13px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-0)]">{turn.text}</div>
      </div>
    </div>
  );
}

function ReasoningBlock({ text }: { text: string }) {
  return (
    <div
      className="mb-3 ml-[39px] mt-1 rounded-[0_8px_8px_0] py-[9px] pl-3.5 pr-3"
      style={{ background: CL_TOKENS.brassSoft, borderLeft: `2px solid ${CL_TOKENS.brass}` }}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <Icon kind="sparkle" size={10} color={CL_TOKENS.brassDeep} stroke={2} />
        <span className="cl-eyebrow text-[9px]" style={{ color: CL_TOKENS.brassDeep }}>
          AI reasoning
        </span>
      </div>
      <div className="text-[12px] font-normal italic leading-[1.5] tracking-[-0.005em]" style={{ color: CL_TOKENS.ink2 }}>
        {text}
      </div>
    </div>
  );
}

function TranscriptSection({ transcript }: { transcript: TranscriptTurn[] | null }) {
  const [showReasoning, setShowReasoning] = useState(false);

  if (!transcript || transcript.length === 0) {
    return (
      <div>
        <div className="cl-eyebrow mb-2.5">Conversation</div>
        <div
          className="rounded-[11px] border border-dashed border-[var(--cream-2)] px-4 py-5 text-center text-[12.5px]"
          style={{ background: CL_TOKENS.surfaceSubtle, color: CL_TOKENS.ink3 }}
        >
          No transcript available for this call.
        </div>
      </div>
    );
  }

  const reasoningCount = transcript.filter(isReasoningTurn).length;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="cl-eyebrow">Conversation</div>
        {reasoningCount > 0 && (
          <button type="button" className="cl-reasoning-toggle" data-on={showReasoning} onClick={() => setShowReasoning((v) => !v)}>
            <Icon kind="sparkle" size={10} stroke={2} />
            {showReasoning ? 'Hide' : 'Show'} AI reasoning
            <span
              className="cl-mono rounded-[4px] px-[5px] text-[10px] font-bold"
              style={{ background: showReasoning ? 'rgba(255,255,255,0.18)' : CL_TOKENS.brassBorder }}
            >
              {reasoningCount}
            </span>
          </button>
        )}
      </div>

      <div className="rounded-[11px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 pb-3 pt-4">
        {transcript.map((turn, i) => {
          if (isReasoningTurn(turn)) {
            return showReasoning ? <ReasoningBlock key={i} text={turn.reasoning} /> : null;
          }
          return <TranscriptTurnView key={i} turn={turn} />;
        })}
      </div>
    </div>
  );
}

/* ---- Trigger card (recall context) -------------------------------------- */
function TriggerCard({ recallContext }: { recallContext: RecallContext }) {
  const typeMeta = RECALL_TYPE_META[recallContext.recallTypeSlug];
  const goalLabel = recallContext.goalType
    ? recallContext.goalType.charAt(0).toUpperCase() + recallContext.goalType.slice(1)
    : null;
  const stepIcon = (type: string): IconKind => (type === 'email' ? 'mail' : type === 'sms' ? 'chat' : 'phone');

  return (
    <div
      className="flex flex-col gap-2.5 rounded-[11px] border px-[15px] py-3.5"
      style={{ background: CL_TOKENS.brassSoft, borderColor: CL_TOKENS.brassBorder }}
    >
      <div className="flex items-center gap-[11px]">
        <div
          className="inline-flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg"
          style={{ background: CL_TOKENS.brassTint, color: CL_TOKENS.brassDeep, border: `1px solid ${CL_TOKENS.brassBorder}` }}
        >
          {/* No exact 'hash' kind — using 'broadcast' to evoke a sequence trigger. */}
          <Icon kind="broadcast" size={13} stroke={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-[7px]">
            <span className="cl-eyebrow text-[9.5px]" style={{ color: CL_TOKENS.brassDeep }}>
              Triggered by
            </span>
            <ConfigPill meta={typeMeta} size="small" />
          </div>
        </div>
        {goalLabel && (
          <span
            className="rounded-[5px] border border-[var(--cream-2)] px-[7px] py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em]"
            style={{ background: CL_TOKENS.surfaceSubtle, color: CL_TOKENS.ink3 }}
          >
            {goalLabel}
          </span>
        )}
      </div>

      <div>
        <div className="text-[13px] font-semibold leading-[1.35] tracking-[-0.005em] text-[var(--ink-0)]">
          {recallContext.recallName}
        </div>
        {recallContext.segmentName && (
          <div className="mt-0.5 text-[11.5px] font-medium tracking-[-0.005em]" style={{ color: CL_TOKENS.ink3 }}>
            {recallContext.segmentName}
          </div>
        )}
      </div>

      {recallContext.priorStepSummary.length > 0 && (
        <div className="mt-0.5 pt-2.5" style={{ borderTop: '1px dashed rgba(168, 132, 92, 0.20)' }}>
          <div className="cl-eyebrow mb-1.5 text-[9.5px]" style={{ color: CL_TOKENS.brassDeep }}>
            Prior steps in this sequence
          </div>
          <div className="flex flex-col gap-[5px]">
            {recallContext.priorStepSummary.map((step, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-[11.5px] font-medium tracking-[-0.005em]"
                style={{ color: CL_TOKENS.ink2 }}
              >
                <Icon kind={stepIcon(step.type)} size={11} color={CL_TOKENS.brassDeep} stroke={1.8} className="flex-none" />
                <span className="capitalize">{step.type}</span>
                <span style={{ color: CL_TOKENS.ink4 }}>·</span>
                <span style={{ color: CL_TOKENS.ink3 }}>{step.result.replace(/_/g, ' ')}</span>
                <span className="ml-auto" style={{ color: CL_TOKENS.ink5 }}>
                  {step.sentAt}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Attempt timeline --------------------------------------------------- */
function AttemptTimeline({ attempts }: { attempts: AttemptLogEntry[] }) {
  return (
    <div>
      <div className="cl-eyebrow mb-2.5">Attempt history</div>
      <div className="overflow-hidden rounded-[11px] border border-[var(--cream-2)] bg-[var(--cream-1)]">
        {attempts.map((a, i) => {
          const isLast = i === attempts.length - 1;
          return (
            <div
              key={i}
              className="grid grid-cols-[28px_1fr_auto] items-center gap-3 px-3.5 py-[11px]"
              style={{ borderBottom: isLast ? 'none' : `1px solid ${CL_TOKENS.border}` }}
            >
              <div
                className="cl-mono inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-bold"
                style={{
                  background: isLast ? CL_TOKENS.green : CL_TOKENS.surfaceMute,
                  color: isLast ? '#fff' : CL_TOKENS.ink3,
                }}
              >
                {a.attempt}
              </div>
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">{a.result}</div>
                <div className="mt-px text-[11px]" style={{ color: CL_TOKENS.ink3 }}>
                  {a.time}
                </div>
              </div>
              <ConfigPill meta={STATUS_META[a.outcome]} size="small" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VoicemailMessageSection({ message }: { message: string }) {
  return (
    <div>
      <div className="cl-eyebrow mb-2.5">Voicemail Elva left</div>
      <div className="relative rounded-[11px] border border-[var(--cream-2)] px-4 py-3.5" style={{ background: CL_TOKENS.surfaceSubtle }}>
        <div className="mb-2 flex items-center gap-[7px]">
          {/* No exact 'voicemail' kind — using 'chat' as the nearest message glyph. */}
          <Icon kind="chat" size={12} color={CL_TOKENS.brassDeep} stroke={1.8} />
          <span className="cl-eyebrow text-[9.5px]" style={{ color: CL_TOKENS.brassDeep }}>
            Audio + transcript below
          </span>
        </div>
        <p className="m-0 text-[13px] italic leading-[1.55] tracking-[-0.005em] text-[var(--ink-0)]">“{message}”</p>
      </div>
    </div>
  );
}

function NoAnswerNote({ attemptNumber, maxAttempts }: { attemptNumber: number; maxAttempts: number }) {
  return (
    <div
      className="rounded-[11px] border border-dashed px-[18px] py-4 text-center"
      style={{ background: CL_TOKENS.surfaceSubtle, borderColor: CL_TOKENS.borderMed }}
    >
      <Icon kind="phone" size={16} color={CL_TOKENS.ink4} stroke={1.8} className="mx-auto mb-2" />
      <div className="mb-1 text-[13px] font-semibold tracking-[-0.005em]" style={{ color: CL_TOKENS.ink2 }}>
        No answer on attempt {attemptNumber}
      </div>
      <div className="text-[12px] tracking-[-0.005em]" style={{ color: CL_TOKENS.ink3 }}>
        {attemptNumber >= maxAttempts
          ? 'This is the final attempt. Patient flagged for staff review.'
          : `Will retry tomorrow morning. Attempt ${attemptNumber + 1} of ${maxAttempts} scheduled.`}
      </div>
    </div>
  );
}

/* ---- Drawer shell ------------------------------------------------------- */
function DrawerShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="cl-drawer flex h-full flex-col overflow-hidden">
      {children}
    </div>
  );
}

function QuickActionRow() {
  return (
    <div className="mt-3.5 flex items-center gap-1.5">
      <button type="button" className="inline-flex h-[30px] items-center gap-1.5 rounded-[7px] border-0 bg-transparent px-2.5 text-[12px] font-medium" style={{ color: CL_TOKENS.ink2, cursor: 'pointer' }}>
        <Icon kind="download" size={11} stroke={2} />
        Download audio
      </button>
      <span className="h-3.5 w-px" style={{ background: CL_TOKENS.borderMed }} />
      <button type="button" className="inline-flex h-[30px] items-center gap-1.5 rounded-[7px] border-0 bg-transparent px-2.5 text-[12px] font-medium" style={{ color: CL_TOKENS.ink2, cursor: 'pointer' }}>
        <Icon kind="team" size={11} stroke={2} />
        Open patient
      </button>
      <span className="h-3.5 w-px" style={{ background: CL_TOKENS.borderMed }} />
      <button type="button" className="inline-flex h-[30px] items-center gap-1.5 rounded-[7px] border-0 bg-transparent px-2.5 text-[12px] font-medium" style={{ color: CL_TOKENS.ink2, cursor: 'pointer' }}>
        <Icon kind="external" size={11} stroke={2} />
        Live Feed
      </button>
    </div>
  );
}

function DrawerHeader({
  name,
  phone,
  primaryMeta,
  secondaryMeta,
  sentiment,
  metaLine,
  onClose,
}: {
  name: string;
  phone: string;
  primaryMeta: React.ReactNode;
  secondaryMeta: React.ReactNode;
  sentiment?: React.ReactNode;
  metaLine: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <header className="flex-none border-b border-[var(--cream-2)] px-[22px] pb-4 pt-5">
      <div className="flex items-start gap-3">
        <PatientAvatar size={44} initials={name === 'Unknown caller' ? null : getInitials(name)} />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 mb-1 text-[20px] font-semibold leading-[1.15] tracking-[-0.025em] text-[var(--ink-0)]">
            {name}
          </h2>
          <div className="cl-mono mb-2 text-[11.5px] font-medium" style={{ color: CL_TOKENS.ink3 }}>
            {phone}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {primaryMeta}
            {secondaryMeta}
            {sentiment}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
          style={{ color: CL_TOKENS.ink2, cursor: 'pointer' }}
          aria-label="Close"
        >
          <Icon kind="x" size={14} stroke={2} />
        </button>
      </div>
      <div
        className="mt-3 flex flex-wrap items-center gap-[7px] text-[12px] font-medium tracking-[-0.005em]"
        style={{ color: CL_TOKENS.ink3 }}
      >
        {metaLine}
      </div>
      <QuickActionRow />
    </header>
  );
}

/* ---- Inbound drawer ----------------------------------------------------- */
export function InboundDrawer({ call, onClose }: { call: InboundCall; onClose: () => void }) {
  return (
    <DrawerShell>
      <DrawerHeader
        name={call.name}
        phone={`${call.phone} · ${call.location}`}
        primaryMeta={<ConfigPill meta={OUTCOME_META[call.outcome]} />}
        secondaryMeta={<ConfigPill meta={INTENT_META[call.intent]} />}
        sentiment={<SentimentDot sentiment={call.sentiment} />}
        metaLine={
          <>
            <Icon kind="clock" size={12} color={CL_TOKENS.ink4} stroke={1.8} />
            <span>{call.timeLabel}</span>
            <span style={{ color: CL_TOKENS.ink5 }}>·</span>
            <span className="cl-mono">{call.duration}</span>
            <span style={{ color: CL_TOKENS.ink5 }}>·</span>
            <span>{call.isReturning ? 'Returning patient' : 'New caller'}</span>
          </>
        }
        onClose={onClose}
      />
      <div className="flex flex-1 flex-col gap-[18px] overflow-auto px-[22px] pb-[22px] pt-4">
        <AudioPlayer duration={call.duration} hasRecording />
        <AICallSummary points={call.summary} />
        <ExtractedSection items={call.extracted} />
        <TranscriptSection transcript={call.transcript} />
      </div>
    </DrawerShell>
  );
}

/* ---- Outbound-patient drawer -------------------------------------------- */
export function OutboundPatientDrawer({ call, onClose }: { call: OutboundPatientCall; onClose: () => void }) {
  const hasRecording = !!(call.transcript || call.voicemailMessage);
  const primaryMeta =
    call.status === 'completed' ? OUTBOUND_OUTCOME_META[call.primaryOutcome] : STATUS_META[call.status];
  return (
    <DrawerShell>
      <DrawerHeader
        name={call.name}
        phone={call.phone}
        primaryMeta={<ConfigPill meta={primaryMeta} />}
        secondaryMeta={<ConfigPill meta={RECALL_TYPE_META[call.recallContext.recallTypeSlug]} />}
        metaLine={
          <>
            <Icon kind="clock" size={12} color={CL_TOKENS.ink4} stroke={1.8} />
            <span>{call.timeLabel}</span>
            <span style={{ color: CL_TOKENS.ink5 }}>·</span>
            <span className="cl-mono">{call.duration}</span>
            <span style={{ color: CL_TOKENS.ink5 }}>·</span>
            <span>
              Attempt {call.attemptNumber} of {call.maxAttempts}
            </span>
          </>
        }
        onClose={onClose}
      />
      <div className="flex flex-1 flex-col gap-[18px] overflow-auto px-[22px] pb-[22px] pt-4">
        <AudioPlayer duration={call.duration} hasRecording={hasRecording} />
        <TriggerCard recallContext={call.recallContext} />
        {call.summary.length > 0 && <AICallSummary points={call.summary} />}
        {call.extracted && call.extracted.length > 0 && <ExtractedSection items={call.extracted} />}
        {call.status === 'voicemail' && call.voicemailMessage && <VoicemailMessageSection message={call.voicemailMessage} />}
        {(call.status === 'no_answer' || call.status === 'failed') && !call.transcript && (
          <NoAnswerNote attemptNumber={call.attemptNumber} maxAttempts={call.maxAttempts} />
        )}
        <AttemptTimeline attempts={call.attemptLog} />
        {call.transcript && <TranscriptSection transcript={call.transcript} />}
      </div>
    </DrawerShell>
  );
}
